import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { Button, Col, Form, FormGroup, Input, Label } from 'reactstrap';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCreditCard, faEye, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import GridContainer from '../../../shared/GridContainer';
import { get } from '../../../../helpers/requests';
import {
  API,
  BASE_URL,
  ESTADO_CUOTA_CREDITO_PAGADO,
  ESTADO_CUOTA_CREDITO_PENDIENTE,
  FORMA_PAGO_LIBRO_VENTAS,
  TIPO_PAGO_PRIMA,
} from '../../../../CONST';
import { vigenciasToOptions } from '../../../../helpers/selects';
import {
  cellDateOperation,
  cellNumOperation,
  cellOperation,
  headerDoubleRowOperation,
  headerOperation,
} from '../../../../helpers/tables';
import { fileDownload, intermediaryAccess } from '../../../../helpers/helpers';
import DownloadBtn from '../../../shared/DownloadBtn';
import ClientSideTable from '../../../shared/ClientSideTable';

const FacturacionCredito = ({ policyId, client, clientAsIntermediaryId }) => {
  const user = useSelector((state) => state.login.user);
  const loginIdToken = useSelector((state) => state.login.id_token);

  const [poliza, setPoliza] = useState({ nemotecnico: '', moneda: '' });
  const [vigencias, setVigencias] = useState({ all: [], selected: null });
  const [cuotas, setCuotas] = useState([]);
  const [loading, setLoading] = useState(true);

  const allVigenciasOpts = vigenciasToOptions(vigencias.all);
  const selectedVigenciaOpt = allVigenciasOpts.find(
    (opt) => opt.value.id === vigencias.selected.id,
  );

  const cuotasParams = `poliza_id=${policyId}&vigencia=${
    vigencias.selected ? vigencias.selected.numero_vigencia : -1
  }`;

  const getClientAuthParams = () => {
    if (clientAsIntermediaryId) {
      return `client_id=${clientAsIntermediaryId}`;
    }
    if (client) {
      return `client_id=${client.id}`;
    }
    return '';
  };

  const getAuthParams = () => {
    let params = getClientAuthParams();
    if (intermediaryAccess(user)) {
      params += params ? `&intermediario_id=${user.organization}` : `intermediario_id=${user.organization}`;
    }
    return params;
  };

  const getCuotasUrl = () => {
    const url = `${BASE_URL}${API.cuotasCredito}/?${cuotasParams}`;
    const authParams = getAuthParams();
    return authParams ? `${url}&${authParams}` : url;
  };

  const getDownloadUrl = (cuotaId) => {
    const url = `${BASE_URL}${API.cuotasCredito}/${cuotaId}/download/`;
    const authParams = getAuthParams();
    return authParams ? `${url}?${authParams}` : url;
  };

  useEffect(() => {
    const clientParams = getClientAuthParams()
    // Buscamos la póliza
    const creditUrl = `${BASE_URL}${API.creditos}/${policyId}`;
    get(clientParams ? `${creditUrl}?${clientParams}` : creditUrl).then((resp) => setPoliza(resp));

    // Buscamos las vigencias
    const vigenciasUrl = `${BASE_URL}${API.simpleEndososCredito}/?poliza_id=${policyId}`;
    get(clientParams ? `${vigenciasUrl}&${clientParams}` : vigenciasUrl).then((resp) =>
      setVigencias({
        all: resp,
        selected: resp.length > 0 ? resp[0] : null,
      }),
    );
  }, [policyId, client]);

  useEffect(() => {
    setLoading(true);
    if (vigencias.selected) {
      get(getCuotasUrl())
        .then((resp) => setCuotas(resp.results))
        .finally(() => setLoading(false));
    }
  }, [vigencias.selected, client]);

  const cuotasPrima = () =>
    cuotas.filter((cuota) => cuota.tipo_pago.nemotecnico === TIPO_PAGO_PRIMA);

  const allCuotasLibroVenta = () =>
    cuotas.length > 0 &&
    cuotasPrima().every((cuota) => cuota.forma_pago.natural_key === FORMA_PAGO_LIBRO_VENTAS);
  const mixedFormaPago = () =>
    !allCuotasLibroVenta() &&
    cuotasPrima().some((cuota) => cuota.forma_pago.natural_key === FORMA_PAGO_LIBRO_VENTAS);

  const handleVigenciaChange = (selectedOption) => {
    setVigencias((prevState) => ({
      ...prevState,
      selected: selectedOption.value,
    }));
  };

  const columns = [
    {
      Header: headerOperation('Tipo de Pago'),
      id: 'tipo_pago',
      accessor: 'tipo_pago.descripcion',
      Cell: (row) => cellOperation(row.value),
      filterable: false,
      sortable: false,
      minWidth: 100,
    },
    {
      Header: headerOperation('Forma de Pago'),
      id: 'forma_pago',
      accessor: 'forma_pago.name',
      Cell: (row) => cellOperation(row.value),
      filterable: false,
      sortable: false,
      minWidth: 100,
    },
    {
      Header: headerOperation('Detalle'),
      id: 'detalle',
      accessor: 'detalle',
      Cell: (row) => cellOperation(row.value),
      filterable: false,
      sortable: false,
      minWidth: 100,
    },
    {
      Header: headerDoubleRowOperation('Fecha', 'Cuota'),
      id: 'fecha_cuota',
      accessor: 'fecha_cuota',
      Cell: (row) => cellDateOperation(row.value),
      filterable: false,
      sortable: false,
      minWidth: 100,
    },
    {
      Header: headerDoubleRowOperation('Fecha', 'Facturación'),
      id: 'fecha_facturacion',
      accessor: 'fecha_facturacion',
      Cell: (row) => cellDateOperation(row.value),
      filterable: false,
      sortable: false,
      minWidth: 100,
    },
    {
      Header: headerDoubleRowOperation('Fecha', 'Vencimiento'),
      id: 'fecha_vencimiento',
      accessor: 'fecha_vencimiento',
      Cell: (row) => cellDateOperation(row.value),
      filterable: false,
      sortable: false,
      minWidth: 100,
    },
    {
      Header: headerDoubleRowOperation('Fecha de', 'Pago'),
      id: 'fecha_pago',
      accessor: 'fecha_pago',
      Cell: (row) => cellDateOperation(row.value),
      filterable: false,
      sortable: false,
      minWidth: 100,
    },
    {
      Header: headerDoubleRowOperation('Monto', 'Neto'),
      id: 'monto_neto',
      accessor: 'monto_neto',
      Cell: (row) => cellNumOperation(row.value),
      filterable: false,
      sortable: false,
      minWidth: 100,
    },
    {
      Header: headerOperation('Factura'),
      id: 'factura',
      Cell: (row) =>
        row.original.factura_id &&
        cellOperation(
          <button
            type="button"
            className="table-link"
            style={{ fontSize: '18px' }}
            onClick={() => fileDownload(getDownloadUrl(row.original.id), loginIdToken)}
            title="descargar"
          >
            <FontAwesomeIcon icon={faFilePdf} />
          </button>,
        ),
      filterable: false,
      sortable: false,
      minWidth: 80,
    },
    {
      Header: headerOperation('Estado'),
      id: 'estado',
      accessor: 'estado',
      Cell: (row) =>
        row.value ? (
          <GridContainer columns="1fr auto" gap="10px" className="table-cell">
            <div>{row.value.descripcion}</div>
            <div style={{ fontSize: '16px' }}>
              {row.value.nemotecnico === ESTADO_CUOTA_CREDITO_PAGADO && (
                <FontAwesomeIcon icon={faCheck} color="green" />
              )}
              {row.value.nemotecnico === ESTADO_CUOTA_CREDITO_PENDIENTE && (
                <FontAwesomeIcon icon={faCreditCard} color="#D32D2D" />
              )}
            </div>
          </GridContainer>
        ) : (
          ''
        ),
      filterable: false,
      sortable: false,
      minWidth: 160,
    },
  ];

  if (mixedFormaPago()) {
    columns.splice(9, 0, {
      Header: headerDoubleRowOperation('Libro de', 'Ventas'),
      id: 'libro_ventas',
      Cell: (cell) =>
        cell.original.tipo_pago.nemotecnico === TIPO_PAGO_PRIMA &&
        cell.original.forma_pago.natural_key === FORMA_PAGO_LIBRO_VENTAS &&
        cellOperation(
          <Link
            to={`/asegurado/libros-venta/${cell.original.poliza}`}
            className="table-link policy"
          >
            <FontAwesomeIcon icon={faEye} />
          </Link>,
        ),
      filterable: false,
      sortable: false,
      minWidth: 80,
    });
  }

  const xlsxUrl = vigencias.selected
    ? `${BASE_URL}${API.cuotasCredito}/download_xlsx/?${cuotasParams}`
    : '';
  const finalXlsxUrl = client ? `${xlsxUrl}&client_id=${client.id}` : xlsxUrl;
  const pdfUrl = vigencias.selected
    ? `${BASE_URL}${API.cuotasCredito}/download_pdf/?${cuotasParams}`
    : '';
  const finalPdfUrl = client ? `${pdfUrl}&client_id=${client.id}` : pdfUrl;

  return (
    <Col md={{ size: 12 }}>
      <GridContainer columns="1fr 1fr" gap="10px 20px">
        <Form>
          <FormGroup row className="mb-0">
            <Label for="poliza" sm={3} className="orsan-label">
              Póliza
            </Label>
            <Col sm={7}>
              <Input id="poliza" plaintext disabled value={poliza.nemotecnico} onChange={() => {}} />
            </Col>
          </FormGroup>
          <FormGroup row className="mb-0">
            <Label for="moneda" sm={3} className="orsan-label">
              Moneda Origen (MO)
            </Label>
            <Col sm={7}>
              <Input id="moneda" plaintext value={poliza.moneda} onChange={() => {}} />
            </Col>
          </FormGroup>
          <FormGroup row className="mb-0">
            <Label for="vigencia" sm={3} className="orsan-label">
              Vigencia
            </Label>
            <Col sm={7}>
              <Select
                id="vigencia"
                name="vigencia"
                className="orsan-form-element"
                value={selectedVigenciaOpt}
                onChange={handleVigenciaChange}
                options={allVigenciasOpts}
                placeholder="Seleccione vigencia"
              />
            </Col>
          </FormGroup>
        </Form>
        <div />
        <GridContainer
          style={{ gridColumn: '1 / 3', marginTop: '30px' }}
          columns={`1fr auto auto${allCuotasLibroVenta() ? ' auto' : ''}`}
          gap="10px"
        >
          <div />
          {allCuotasLibroVenta() && (
            <div>
              <Link to={`/asegurado/libros-venta/${policyId}`}>
                <Button color="orsan-primary" outline style={{ fontSize: '14px', width: '170px' }}>
                  Libro de Ventas
                </Button>
              </Link>
            </div>
          )}
          <div>
            <DownloadBtn
              id="xlsx-download"
              text="XLSX"
              tipText="Descargar planilla excel"
              url={xlsxUrl ? finalXlsxUrl : ''}
            />
          </div>
          <div>
            <DownloadBtn
              id="pdf-download"
              text="PDF"
              tipText="Descargar PDF"
              url={pdfUrl ? finalPdfUrl : ''}
            />
          </div>
        </GridContainer>
        <div style={{ gridColumn: '1 / 3' }}>
          <ClientSideTable
            columns={columns}
            data={cuotas}
            pageSize={13}
            loading={loading}
            style={{ maxHeight: '63vh' }}
          />
        </div>
        <div style={{ gridColumn: '1 / 3' }}>
          <Link to="/asegurado/polizas-credito">
            <Button
              className="float-right"
              color="orsan-secondary"
              style={{ width: '150px', fontSize: '14px' }}
            >
              Volver
            </Button>
          </Link>
        </div>
      </GridContainer>
    </Col>
  );
};

FacturacionCredito.propTypes = {
  policyId: PropTypes.string.isRequired,
  client: PropTypes.shape({
    id: PropTypes.number,
  }),
  clientAsIntermediaryId: PropTypes.string,
};

FacturacionCredito.defaultProps = {
  client: null,
  clientAsIntermediaryId: null,
};

export default FacturacionCredito;
